import { Trans } from "react-i18next";import { X } from "lucide-react";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle } from
"../../../components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { LeaveStatusBadge } from "./LeaveStatusBadge";
import { LeaveBalanceValue } from "./LeaveBalanceValue";
import { getEmployeeLeaveRowAction } from "./employeeLeaveRowAction";

function DetailField({ label, children, className }) {
  return (
    <div className={cn("space-y-0.5", className)}>
      <span className="block text-[10px] font-bold uppercase tracking-wider per-file-hint">
        {label}
      </span>
      <div className="text-sm text-foreground">{children}</div>
    </div>);

}

function LeaveTypeBadge({ label }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-workspace/80 text-primary">
      {label}
    </span>);

}

export function LeaveDetailsModal({
  open,
  onOpenChange,
  leave,
  onWithdraw,
  onRevoke,
  actionLoading = false
}) {
  if (!leave) return null;

  const rowAction = getEmployeeLeaveRowAction(leave.status);
  const daysLabel = leave.days === 1 ? "Day" : "Days";
  const managerRemarks = leave.actionByRemarks?.trim() || null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-xs" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[520px] max-w-[520px]",
            "max-h-[90vh] overflow-hidden flex flex-col",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "font-sans"
          )}>
          
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-(--color-text-border)/70 bg-white px-4 py-3 shrink-0">
            <DialogTitle className="text-[18px] font-semibold text-foreground tracking-tight">
              <Trans i18nKey="leave.leaveDetails" />
            </DialogTitle>
            <DialogPrimitive.Close
              disabled={actionLoading}
              className="h-8 w-8 grid place-items-center rounded-lg per-file-hint hover:bg-workspace/60 hover:text-primary transition disabled:opacity-50">
              
              <X className="h-4 w-4" />
              <span className="sr-only"><Trans i18nKey="formActions.close" /></span>
            </DialogPrimitive.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 space-y-3">
            <div className="rounded-xl border border-(--color-text-border)/40 bg-workspace/30 px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <LeaveTypeBadge label={leave.type} />
                <LeaveStatusBadge status={leave.status} />
              </div>
            </div>

            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider per-file-hint mb-2">
                <Trans i18nKey="leave.leaveInformation" />
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 rounded-xl border border-(--color-text-border)/40 px-3 py-2.5">
                {leave.leaveId != null ?
                <DetailField label="Leave ID" className="col-span-2 sm:col-span-1">
                    <span className="font-semibold text-primary">#{leave.leaveId}</span>
                  </DetailField> :
                null}
                
                <DetailField label="From Date">{leave.from || "—"} ({leave.fromSession || "—"})</DetailField>
                <DetailField label="To Date">{leave.to || "—"} ({leave.toSession || "—"})</DetailField>
                <DetailField label="Total Days">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-workspace/60 text-primary text-xs font-semibold">
                    {leave.days} {daysLabel}
                  </span>
                </DetailField>
                <DetailField label="Leave Balance">
                  <span className="font-medium text-primary">
                    <LeaveBalanceValue
                      empId={leave.empId}
                      leaveTypeId={leave.leaveTypeId}
                      enabled={open}
                      fallbackToSession />
                    
                  </span>
                </DetailField>
                <DetailField label="Applied Date">{leave.appliedOn || "—"}</DetailField>
              </div>
            </div>

            <div className="rounded-xl border border-(--color-text-border)/40 px-3 py-2.5">
              <p className="text-[10px] font-bold uppercase tracking-wider per-file-hint mb-1.5">
                <Trans i18nKey="common.reason" />
              </p>
              <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap wrap-break-word">
                {leave.reason?.trim() ? leave.reason : "—"}
              </p>
            </div>

            {managerRemarks ?
            <div className="rounded-xl border border-(--color-text-border)/40 px-3 py-2.5">
                <p className="text-[10px] font-bold uppercase tracking-wider per-file-hint mb-1.5">
                  <Trans i18nKey="common.managerRemarks" />
                </p>
                <p className="text-sm per-file-hint leading-relaxed whitespace-pre-wrap wrap-break-word">
                  {managerRemarks}
                </p>
              </div> :
            null}
          </div>

          {rowAction ?
          <div className="shrink-0 border-t border-(--color-text-border)/70 px-4 py-3">
              {rowAction === "withdraw" ?
            <button
              type="button"
              onClick={() => onWithdraw?.(leave)}
              disabled={actionLoading}
              className="h-10 w-full rounded-xl border border-error/30 text-error text-sm font-semibold hover:bg-error hover:text-white transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                  <Trans i18nKey="formActions.withdrawLeave" />
                
            </button> :

            <button
              type="button"
              onClick={() => onRevoke?.(leave)}
              disabled={actionLoading}
              className="h-10 w-full rounded-xl border border-warning/40 text-warning text-sm font-semibold hover:bg-warning hover:text-white transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                  <Trans i18nKey="formActions.revokeLeave" />
                
            </button>
            }
            </div> :
          null}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>);

}
